import { systemApi } from '@/api'
import { typeList } from '@/api/englishLearning/course'
import errorLogger from '@/utils/error-report/errorLogger.js'

/** 是否「课程广场」类顶级分类（侧栏合并入口） */
function isCoursePlazaTypeTitle (title) {
  const t = String(title || '')
  if (t.includes('口语')) return false
  return t.includes('课程广场') || t.includes('广场')
}

/** 是否「考证考级」类顶级分类（并入课程广场入口，列表内切换） */
function isCertTypeTitle (title) {
  const t = String(title || '')
  return t.includes('考证') || t.includes('考级')
}

/**
 * 将课程广场与考证考级合并为侧栏单一入口「课程广场」，
 * switchGroup 供列表页顶栏切换；其余顶级分类（如教材同步）保持独立菜单。
 */
function mergeCoursePlazaMenuItems (menuItems) {
  const list = Array.isArray(menuItems) ? menuItems.filter(Boolean) : []
  const plazaGroup = list.filter(
    (item) => isCoursePlazaTypeTitle(item.label) || isCertTypeTitle(item.label)
  )
  if (!plazaGroup.length) return list

  const primary =
    plazaGroup.find((x) => isCoursePlazaTypeTitle(x.label)) || plazaGroup[0]
  const switchGroup = plazaGroup.map((x) => ({
    id: x.query?.id,
    title: isCoursePlazaTypeTitle(x.label)
      ? '课程广场'
      : isCertTypeTitle(x.label)
        ? '考证考级'
        : x.label,
    icon: x.icon
  })).filter((x) => x.id !== undefined && x.id !== null && x.id !== '')

  const merged = {
    ...primary,
    label: '课程广场',
    query: primary.query?.id != null ? { id: primary.query.id } : (primary.query || {}),
    switchGroup: switchGroup.length ? switchGroup : undefined
  }

  // 保持原动态菜单相对顺序：合并项插在第一个被合并项的位置
  const firstIdx = list.findIndex(
    (x) => isCoursePlazaTypeTitle(x.label) || isCertTypeTitle(x.label)
  )
  const out = []
  let inserted = false
  list.forEach((item, idx) => {
    if (isCoursePlazaTypeTitle(item.label) || isCertTypeTitle(item.label)) {
      if (!inserted && idx === firstIdx) {
        out.push(merged)
        inserted = true
      }
      return
    }
    out.push(item)
  })
  if (!inserted) out.unshift(merged)
  return out
}

const state = {
  // 系统基础配置（启动即加载，供业务使用）
  systemConf: JSON.parse(localStorage.getItem('systemConf') || '{}'),
  // 主题模式：light | dark | system
  themeMode: localStorage.getItem('themeMode') || 'system',
  // 左侧菜单项布局：column=图标上文字下 | row=图标与文字同行（默认 row）
  sidebarMenuLayout: localStorage.getItem('sidebarMenuLayout') === 'column' ? 'column' : 'row',
  // 设备信息面板显示状态
  deviceInfoVisible: false,
  // 菜单数据 - 初始静态菜单（与 getMenu fallback 保持一致，避免接口加载前菜单不完整）
  menu: [
    {
      label: '我的课程',
      path: '/englishLearning/myCourse',
      icon: 'icon-bt_cb_tab_2'
    },
    {
      label: '错题本',
      path: '/englishLearning/learningRecord',
      icon: 'icon-bt_cb_tab_6'
    },
    {
      label: '查词',
      path: '/englishLearning/wordLookup',
      icon: 'icon-bt_cb_tab_11'
    },
    {
      label: '字帖',
      path: '/englishLearning/writePaper',
      icon: 'icon-bt_cb_tab_10'
    },
    {
      label: '口语广场',
      path: '/englishLearning/speakingPlaza',
      icon: 'icon-bt_cb_tab_8'
    },
    {
      label: '自然拼读',
      path: '/englishLearning/phonics',
      icon: 'icon-bt_cb_tab_12'
    },
    {
      label: 'AI创课',
      path: '/englishLearning/aICreationWorkshop',
      icon: 'icon-bt_cb_tab_7'
    },
    {
      label: '排行',
      path: '/englishLearning/rankingList',
      icon: 'icon-bt_cb_tab_8'
    },
    {
      label: '合伙人',
      path: '/englishLearning/honorPartner',
      icon: 'icon-bt_cb_tab_9'
    }
  ]
}

const getters = {
  systemConf: state => state.systemConf,
  themeMode: state => state.themeMode,
  sidebarMenuLayout: state => state.sidebarMenuLayout,
  deviceInfoVisible: state => state.deviceInfoVisible,
  menu: state => state.menu
}

const mutations = {
  SET_SYSTEM_CONF (state, conf) {
    state.systemConf = conf || {}
    localStorage.setItem('systemConf', JSON.stringify(state.systemConf))
  },
  SET_THEME_MODE (state, mode) {
    state.themeMode = mode
    localStorage.setItem('themeMode', mode)
  },
  SET_SIDEBAR_MENU_LAYOUT (state, layout) {
    const next = layout === 'column' ? 'column' : 'row'
    state.sidebarMenuLayout = next
    localStorage.setItem('sidebarMenuLayout', next)
  },
  setDeviceInfoVisible (state, visible) {
    state.deviceInfoVisible = visible
  },
  SET_MENU (state, menu) {
    state.menu = menu || []
  }
}

const actions = {
  // 获取系统配置
  async getSystemConf ({ commit }) {
    try {
      const res = await systemApi.getConfig()
      if (res && res.code === 1) {
        const confData = res.data || {}
        commit('SET_SYSTEM_CONF', confData)
        errorLogger.onSystemConfUpdated(confData)
        return Promise.resolve(confData)
      }
      return Promise.resolve({})
    } catch (error) {
      console.error('获取系统配置失败:', error)
      return Promise.resolve({})
    }
  },
  setThemeMode ({ commit }, mode) {
    commit('SET_THEME_MODE', mode)
  },
  setSidebarMenuLayout ({ commit }, layout) {
    commit('SET_SIDEBAR_MENU_LAYOUT', layout)
  },
  /** 从 set_show 同步左侧菜单对齐（有字段则以接口为准） */
  async syncSidebarMenuLayoutFromApi ({ commit, rootGetters }) {
    const token = rootGetters['user/token']
    if (!token) return
    try {
      const { getUserSettings } = await import('@/api/modules/user')
      const res = await getUserSettings({})
      if (res?.code !== 1 || !res.data) return
      if (res.data.sidebar_menu_layout === undefined || res.data.sidebar_menu_layout === null) return
      const next = String(res.data.sidebar_menu_layout).trim() === 'column' ? 'column' : 'row'
      commit('SET_SIDEBAR_MENU_LAYOUT', next)
    } catch (e) {
      console.warn('同步左侧菜单对齐失败:', e)
    }
  },
  // 获取动态菜单（从接口获取顶级分类）
  async getMenu ({ commit, state }) {
    try {
      // 静态菜单
      const staticMenu = [
        {
          label: '我的课程',
          path: '/englishLearning/myCourse',
          icon: 'icon-bt_cb_tab_2'
        }
      ]

      // 从接口获取顶级分类列表
      let dynamicMenu = []
      try {
        const typeRes = await typeList({})
        if (typeRes?.code === 1) {
          const typeListItems = typeRes.data || []
          // 使用接口返回的 route_path 字段
          dynamicMenu = typeListItems
            .filter(item => {
              // 过滤无效数据
              if (!item || !item.title) {
                return false
              }
              // route_path 可能是完整路径，也可能需要拼接
              if (!item.route_path && !item.route_name) {
                return false
              }
              return true
            })
            .map((item) => {
              // 优先使用 route_path，如果没有则使用 route_name 拼接
              let path = item.route_path || `/englishLearning/${item.route_name}`

              // 如果 route_path 包含 /index，去掉 /index
              if (path.endsWith('/index')) {
                path = path.replace(/\/index$/, '')
              }

              const menuItem = {
                label: item.title,
                path,
                icon: item.icon || 'icon-bt_cb_tab_3',
                // 将后端 id 作为 query 参数传递
                query: item.id ? { id: item.id } : {}
              }

              return menuItem
            })
          // 课程广场 + 考证考级 → 侧栏只保留「课程广场」，列表页用 switchGroup 切换
          dynamicMenu = mergeCoursePlazaMenuItems(dynamicMenu)
        }
      } catch (error) {
        console.error('获取顶级分类列表失败:', error)
        // 接口失败不影响静态菜单
      }

      // 底部固定菜单
      const bottomMenu = [
        {
          label: '口语广场',
          path: '/englishLearning/speakingPlaza',
          icon: 'icon-bt_cb_tab_8'
        },
        {
          label: '错题本',
          path: '/englishLearning/learningRecord',
          icon: 'icon-bt_cb_tab_6'
        },
        {
          label: '查词',
          path: '/englishLearning/wordLookup',
          icon: 'icon-bt_cb_tab_11'
        },
        {
          label: '字帖',
          path: '/englishLearning/writePaper',
          icon: 'icon-bt_cb_tab_10'
        },
        {
          label: '自然拼读',
          path: '/englishLearning/phonics',
          icon: 'icon-bt_cb_tab_12'
        },
        {
          label: 'AI创课',
          path: '/englishLearning/aICreationWorkshop',
          icon: 'icon-bt_cb_tab_7'
        },
        {
          label: '排行',
          path: '/englishLearning/rankingList',
          icon: 'icon-bt_cb_tab_8'
        },
        {
          label: '合伙人',
          path: '/englishLearning/honorPartner',
          icon: 'icon-bt_cb_tab_9'
        }
      ]

      // 合并所有菜单并过滤 null/undefined
      const menu = [...staticMenu, ...dynamicMenu, ...bottomMenu].filter(item => item && item.label && item.path)

      // console.log('最终菜单数据:', menu)
      commit('SET_MENU', menu)
      return Promise.resolve(menu)
    } catch (error) {
      console.error('获取菜单失败:', error)
      // 失败时返回静态菜单
      const fallbackMenu = [
        {
          label: '我的课程',
          path: '/englishLearning/myCourse',
          icon: 'icon-bt_cb_tab_2'
        },
        {
          label: '口语广场',
          path: '/englishLearning/speakingPlaza',
          icon: 'icon-bt_cb_tab_8'
        },
        {
          label: '错题本',
          path: '/englishLearning/learningRecord',
          icon: 'icon-bt_cb_tab_6'
        },
        {
          label: '查词',
          path: '/englishLearning/wordLookup',
          icon: 'icon-bt_cb_tab_11'
        },
        {
          label: '字帖',
          path: '/englishLearning/writePaper',
          icon: 'icon-bt_cb_tab_10'
        },
        {
          label: '自然拼读',
          path: '/englishLearning/phonics',
          icon: 'icon-bt_cb_tab_12'
        },
        {
          label: 'AI创课',
          path: '/englishLearning/aICreationWorkshop',
          icon: 'icon-bt_cb_tab_7'
        },
        {
          label: '排行',
          path: '/englishLearning/rankingList',
          icon: 'icon-bt_cb_tab_8'
        },
        {
          label: '合伙人',
          path: '/englishLearning/honorPartner',
          icon: 'icon-bt_cb_tab_9'
        }
      ]
      commit('SET_MENU', fallbackMenu)
      return Promise.resolve(fallbackMenu)
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
